import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, Plus, X, CloudCog, RefreshCw, DatabaseZap } from "lucide-react";
import { useMasters } from "../form/engine";
import { formatApiError } from "../../contexts/AuthContext";
import { AuditorDashboard, EmergencyAccess } from "./GovernanceAdmin";
import { DealRoomPanel } from "./DealRoom";
import { EnterpriseAdmin } from "./EnterpriseAdmin";
import { TicketConsole, SessionTracker, DataExport } from "./SupportConsole";
import { PermissionMatrix } from "./PermissionMatrix";
import { WorldMapSessions } from "./WorldMapSessions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const ErpStatus = () => {
  const [status, setStatus] = useState(null);
  const [draining, setDraining] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/erp/status`, headers()).then((r) => setStatus(r.data)).catch(() => setStatus(null));
  };
  useEffect(load, []);

  const drain = async () => {
    setDraining(true);
    try {
      const { data } = await axios.post(`${API}/admin/erp/drain`, {}, headers());
      toast.success(`Synced ${data.synced} records to Zoho${data.failed ? ` · ${data.failed} failed` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDraining(false);
    }
  };

  if (!status) return null;
  return (
    <section className="rounded-3xl border border-[#7C3AED]/25 bg-[#7C3AED]/[0.05] p-6 backdrop-blur-xl" data-testid="erp-status">
      <p className="flex items-center gap-2 font-display text-lg font-bold">
        <CloudCog className="h-5 w-5 text-[#A78BFA]" /> Zoho + SAP Sync
      </p>
      <div className="mt-4 grid grid-cols-3 gap-3">
        {[
          { label: "Queued", value: status.queued },
          { label: "Synced", value: status.synced },
          { label: "Failed", value: status.failed },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-white/10 bg-[#09090B]/60 p-4 text-center">
            <p className="font-display text-2xl font-extrabold">{s.value}</p>
            <p className="mt-1 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className={`rounded-full px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-wider ${status.zoho_configured ? "bg-[#06B6D4]/10 text-[#06B6D4]" : "bg-[#F97316]/10 text-[#F97316]"}`} data-testid="zoho-status">
          Zoho {status.zoho_configured ? "connected" : "keys missing"}
        </span>
        <span className={`rounded-full px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-wider ${status.sap_configured ? "bg-[#06B6D4]/10 text-[#06B6D4]" : "bg-[#F97316]/10 text-[#F97316]"}`} data-testid="sap-status">
          SAP {status.sap_configured ? "connected" : "keys missing"}
        </span>
        <button
          data-testid="erp-drain"
          onClick={drain}
          disabled={draining || !status.queued}
          className="ml-auto flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
        >
          {draining ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Sync queued records
        </button>
      </div>
      <p className="mt-3 font-jbmono text-[10px] uppercase tracking-widest text-slate-600">
        Records carry pre-mapped Zoho/SAP payloads from onboarding · activates when keys are added
      </p>
    </section>
  );
};

const CertificationsAdmin = () => {
  const [certs, setCerts] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    axios.get(`${API}/admin/certifications`, headers()).then((r) => setCerts(r.data.certifications)).catch(() => setCerts([]));
  };
  useEffect(load, []);

  const review = async (userId, action) => {
    setBusyId(userId);
    try {
      await axios.patch(`${API}/admin/certifications/${userId}`, { action }, headers());
      toast.success(action === "approve" ? "Chef certified ✅ Badge is live." : "Certification rejected");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (!certs) return null;
  const pending = certs.filter((c) => c.status === "pending");
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="cert-admin">
      <p className="font-display text-lg font-bold">✅ Chef Certifications ({pending.length} pending)</p>
      {certs.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No certification submissions yet.</p>
      ) : (
        <div className="mt-4 space-y-2.5">
          {certs.map((c) => (
            <div key={c.user_id} data-testid={`cert-row-${c.user_id.slice(-6)}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold">{c.name || c.email}</p>
                <p className="font-jbmono text-[10px] text-slate-500">
                  {Object.values(c.checklist || {}).filter(Boolean).length}/5 training · {c.id_doc_url ? "ID uploaded" : "no ID"}
                </p>
              </div>
              {c.id_doc_url && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}${c.id_doc_url}`} target="_blank" rel="noreferrer" data-testid={`cert-view-id-${c.user_id.slice(-6)}`} className="rounded-full border border-white/15 px-3 py-1.5 text-[10px] font-bold text-slate-300 hover:text-white">
                  View ID
                </a>
              )}
              <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                c.status === "approved" ? "bg-[#06B6D4]/10 text-[#06B6D4]" :
                c.status === "pending" ? "bg-[#22C55E]/10 text-[#22C55E]" :
                c.status === "rejected" ? "bg-[#F97316]/10 text-[#F97316]" : "bg-white/5 text-slate-500"
              }`}>{c.status}</span>
              {c.status === "pending" && (
                <>
                  <button data-testid={`cert-approve-${c.user_id.slice(-6)}`} onClick={() => review(c.user_id, "approve")} disabled={busyId === c.user_id} className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-white disabled:opacity-50">
                    Approve
                  </button>
                  <button data-testid={`cert-reject-${c.user_id.slice(-6)}`} onClick={() => review(c.user_id, "reject")} disabled={busyId === c.user_id} className="rounded-full border border-[#F97316]/40 px-4 py-1.5 text-[10px] font-bold text-[#F97316]">
                    Reject
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const PayoutsAdmin = () => {
  const [payouts, setPayouts] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    axios.get(`${API}/admin/payouts`, headers()).then((r) => setPayouts(r.data.payouts)).catch(() => setPayouts([]));
  };
  useEffect(load, []);

  const pay = async (id) => {
    setBusyId(id);
    try {
      await axios.patch(`${API}/admin/payouts/${id}`, {}, headers());
      toast.success("Payout marked paid 💸 Chef notified.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (!payouts) return null;
  const pending = payouts.filter((p) => p.status === "requested");
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="payouts-admin">
      <p className="font-display text-lg font-bold">💸 Chef Payouts ({pending.length} to process)</p>
      {payouts.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No payout requests yet.</p>
      ) : (
        <div className="mt-4 space-y-2.5">
          {payouts.map((p) => (
            <div key={p.id} data-testid={`payout-row-${p.id.slice(-6)}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
              <span className="font-jbmono text-sm font-bold text-[#22C55E]">₹{p.amount.toLocaleString()}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold">{p.name || p.email}</p>
                <p className="font-jbmono text-[10px] text-slate-600">{String(p.created_at).slice(0, 10)}</p>
              </div>
              <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                p.status === "paid" ? "bg-[#06B6D4]/10 text-[#06B6D4]" : "bg-[#22C55E]/10 text-[#22C55E]"
              }`}>{p.status}</span>
              {p.status === "requested" && (
                <button data-testid={`payout-pay-${p.id.slice(-6)}`} onClick={() => pay(p.id)} disabled={busyId === p.id} className="rounded-full bg-gradient-to-r from-[#22C55E] to-[#F97316] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-[#09090B] disabled:opacity-50">
                  Mark paid
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const KIND_EMOJI = { kitchen: "👨‍🍳", hotel: "🏨", restaurant: "🍽️", store: "🛒" };

const BusinessAdmin = () => {
  const [orgs, setOrgs] = useState(null);
  const [form, setForm] = useState({ name: "", kind: "kitchen", admin_email: "", admin_password: "", admin_name: "", city: "", country: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/admin/org`, headers()).then((r) => setOrgs(r.data.orgs)).catch(() => setOrgs([]));
  };
  useEffect(load, []);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/org`, form, headers());
      toast.success(
        data.created_account
          ? `${data.org.name} created — share the temp password with ${data.org.name}'s admin.`
          : `${data.org.name} created and assigned.`
      );
      setForm({ name: "", kind: "kitchen", admin_email: "", admin_password: "", admin_name: "", city: "", country: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (id, action) => {
    try {
      await axios.patch(`${API}/admin/org/${id}/verify`, { action }, headers());
      toast.success(action === "approve" ? "Business verified ✅" : "Business rejected");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (orgs === null) return null;
  const pending = orgs.filter((o) => o.status === "pending");
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="org-admin">
      <p className="font-display text-lg font-bold">🏢 Business Admins ({orgs.length}) · {pending.length} pending verification</p>
      <p className="mt-1 text-xs text-slate-500">Create kitchen, hotel, restaurant and store admins — FUNZ business emails (@funz.store) only.</p>
      <div className="mt-4 grid gap-2 sm:grid-cols-3" data-testid="org-create">
        <input data-testid="org-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Business name"
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
        <select data-testid="org-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white outline-none focus:border-[#7C3AED]/60">
          <option value="kitchen">Kitchen</option>
          <option value="hotel">Hotel</option>
          <option value="restaurant">Restaurant</option>
          <option value="store">Store</option>
        </select>
        <input data-testid="org-admin-email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="admin@funz.store"
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
        <input data-testid="org-admin-name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} placeholder="Admin name (new accounts)"
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
        <input data-testid="org-admin-password" type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} placeholder="Temp password (new accounts)"
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
        <input data-testid="org-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City, Country"
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60"
          onBlur={(e) => { const [c, co] = e.target.value.split(","); setForm({ ...form, city: (c || "").trim(), country: (co || "").trim() }); }} />
      </div>
      <button data-testid="org-create-btn" onClick={create} disabled={busy || !form.name.trim() || !form.admin_email.trim()}
        className="mt-3 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
        {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create business admin
      </button>
      <div className="mt-5 space-y-2.5">
        {orgs.map((o) => (
          <div key={o.id} data-testid={`org-row-${o.id.slice(-6)}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
            <span className="text-2xl">{KIND_EMOJI[o.kind] || "🏢"}</span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold">{o.name}</p>
              <p className="truncate font-jbmono text-[10px] text-slate-500">
                {o.kind} · {o.owner_email || "no owner"} · {o.staff_count} staff{[o.city, o.country].filter(Boolean).length ? ` · ${[o.city, o.country].filter(Boolean).join(", ")}` : ""}
              </p>
            </div>
            <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
              o.status === "verified" ? "bg-[#22C55E]/10 text-[#22C55E]" : o.status === "pending" ? "bg-[#F97316]/10 text-[#F97316]" : "bg-[#EF4444]/10 text-[#EF4444]"
            }`}>{o.status}</span>
            {o.status === "pending" && (
              <>
                <button data-testid={`org-approve-${o.id.slice(-6)}`} onClick={() => verify(o.id, "approve")} className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-white">
                  Verify
                </button>
                <button data-testid={`org-reject-${o.id.slice(-6)}`} onClick={() => verify(o.id, "reject")} className="rounded-full border border-[#F97316]/40 px-4 py-1.5 text-[10px] font-bold text-[#F97316]">
                  Reject
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const IdVerificationsAdmin = () => {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    axios.get(`${API}/admin/org/verifications`, headers()).then((r) => setItems(r.data.verifications)).catch(() => setItems([]));
  };
  useEffect(load, []);

  const review = async (userId, action) => {
    setBusyId(userId);
    try {
      await axios.patch(`${API}/admin/org/verifications/${userId}`, { action }, headers());
      toast.success(action === "approve" ? "ID verified ✅ Trust Score boosted." : "Verification rejected");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (items === null) return null;
  const pending = items.filter((i) => i.status === "pending");
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="id-admin">
      <p className="font-display text-lg font-bold">🪪 ID Verifications ({pending.length} pending)</p>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No ID submissions yet.</p>
      ) : (
        <div className="mt-4 space-y-2.5">
          {items.map((v) => (
            <div key={v.user_id} data-testid={`id-row-${v.user_id.slice(-6)}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold">{v.name || v.email}</p>
                <p className="font-jbmono text-[10px] text-slate-500">{v.id_type.replace("_", " ")} · {String(v.submitted_at).slice(0, 10)}</p>
              </div>
              {v.doc_url && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}${v.doc_url}`} target="_blank" rel="noreferrer" data-testid={`id-view-${v.user_id.slice(-6)}`} className="rounded-full border border-white/15 px-3 py-1.5 text-[10px] font-bold text-slate-300 hover:text-white">
                  View ID
                </a>
              )}
              <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                v.status === "verified" ? "bg-[#22C55E]/10 text-[#22C55E]" : v.status === "pending" ? "bg-[#F97316]/10 text-[#F97316]" : "bg-[#EF4444]/10 text-[#EF4444]"
              }`}>{v.status}</span>
              {v.status === "pending" && (
                <>
                  <button data-testid={`id-approve-${v.user_id.slice(-6)}`} onClick={() => review(v.user_id, "approve")} disabled={busyId === v.user_id} className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-white disabled:opacity-50">
                    Approve
                  </button>
                  <button data-testid={`id-reject-${v.user_id.slice(-6)}`} onClick={() => review(v.user_id, "reject")} disabled={busyId === v.user_id} className="rounded-full border border-[#F97316]/40 px-4 py-1.5 text-[10px] font-bold text-[#F97316]">
                    Reject
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const GovernanceAdmin = () => {
  const [alerts, setAlerts] = useState(null);
  const [audit, setAudit] = useState(null);
  const [tab, setTab] = useState("alerts");

  const load = () => {
    axios.get(`${API}/admin/governance/alerts`, headers()).then((r) => setAlerts(r.data.alerts)).catch(() => setAlerts([]));
    axios.get(`${API}/admin/governance/audit?limit=50`, headers()).then((r) => setAudit(r.data.audit)).catch(() => setAudit([]));
  };
  useEffect(load, []);

  const resolve = async (id) => {
    try {
      await axios.patch(`${API}/admin/governance/alerts/${id}`, {}, headers());
      toast.success("Alert resolved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const open = (alerts || []).filter((a) => a.status === "open");
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="governance-admin">
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-display text-lg font-bold">⚖️ Governance</p>
        <span className={`rounded-full px-3 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${open.length ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#22C55E]/10 text-[#22C55E]"}`} data-testid="alert-count">
          {open.length} open alerts
        </span>
        <div className="ml-auto flex gap-2">
          {["alerts", "audit"].map((t) => (
            <button key={t} data-testid={`gov-tab-${t}`} onClick={() => setTab(t)}
              className={`rounded-full px-4 py-1.5 font-jbmono text-[10px] font-bold uppercase tracking-wider ${tab === t ? "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white" : "border border-white/10 text-slate-400"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {tab === "alerts" ? (
        alerts === null ? <LoaderCircle className="mt-4 h-5 w-5 animate-spin text-[#06B6D4]" /> : alerts.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">No fraud alerts — the platform is clean.</p>
        ) : (
          <div className="mt-4 space-y-2.5">
            {alerts.map((a) => (
              <div key={a.id} data-testid={`alert-${a.id.slice(-6)}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
                <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${a.status === "open" ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#22C55E]/10 text-[#22C55E]"}`}>{a.status}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold">{a.kind.replace(/_/g, " ")}</p>
                  <p className="truncate font-jbmono text-[10px] text-slate-500">{a.detail} · {String(a.at).slice(0, 16).replace("T", " ")}</p>
                </div>
                {a.status === "open" && (
                  <button data-testid={`alert-resolve-${a.id.slice(-6)}`} onClick={() => resolve(a.id)} className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-white">
                    Resolve
                  </button>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        audit === null ? <LoaderCircle className="mt-4 h-5 w-5 animate-spin text-[#06B6D4]" /> : audit.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">No audit entries yet.</p>
        ) : (
          <div className="mt-4 space-y-1.5">
            {audit.map((e) => (
              <div key={e.id} data-testid={`audit-${e.id.slice(-6)}`} className="flex items-center gap-3 rounded-xl border border-white/5 bg-[#09090B]/60 px-4 py-2.5">
                <span className="rounded-full bg-[#06B6D4]/10 px-2.5 py-0.5 font-jbmono text-[9px] font-bold uppercase tracking-wider text-[#06B6D4]">{e.action}</span>
                <p className="min-w-0 flex-1 truncate font-jbmono text-[10px] text-slate-500">{e.target_type}{e.detail ? ` · ${e.detail}` : ""}</p>
                <span className="font-jbmono text-[9px] text-slate-600">{String(e.at).slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        )
      )}
    </section>
  );
};

const FinanceConsole = () => {
  const [queue, setQueue] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [dealRoom, setDealRoom] = useState(null);

  const load = () => {
    axios.get(`${API}/admin/governance/finance/queue`, headers()).then((r) => setQueue(r.data.queue)).catch(() => setQueue([]));
  };
  useEffect(load, []);

  const approve = async (id, role) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/governance/finance/approve/${id}`, {}, { ...headers(), params: { note: "approved by " + role } });
      toast.success(`${role} review recorded`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (queue === null) return null;
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="finance-console">
      {dealRoom && <DealRoomPanel pitchId={dealRoom} onClose={() => { setDealRoom(null); load(); }} />}
      <p className="font-display text-lg font-bold">🏦 Finance Approvals ({queue.filter((q) => q.status !== "approved").length} pending)</p>
      <p className="mt-1 text-xs text-slate-500">High-risk funding & pitches need dual approval — Finance Controller + Admin — before release.</p>
      {queue.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No high-risk items in the queue.</p>
      ) : (
        <div className="mt-4 space-y-2.5">
          {queue.map((q) => (
            <div key={q.id} data-testid={`finance-${q.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold">{q.option_name}</p>
                  <p className="truncate font-jbmono text-[10px] text-slate-500">{q.applicant} · ₹{Number(q.amount).toLocaleString()} · {q.status.replace("_", " ")}</p>
                </div>
                <div className="flex gap-1.5">
                  {["finance_controller", "admin"].map((role) => (
                    <span key={role} className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                      q.reviews.some((r) => r.role === role) ? "bg-[#22C55E]/10 text-[#22C55E]" : "bg-white/5 text-slate-500"
                    }`}>
                      {role.replace("_", " ")}{q.reviews.some((r) => r.role === role) ? " ✓" : ""}
                    </span>
                  ))}
                </div>
              </div>
              {q.status !== "approved" && (
                <div className="mt-3 flex gap-2">
                  <button data-testid={`finance-approve-fc-${q.id.slice(-6)}`} onClick={() => approve(q.id, "finance_controller")} disabled={busyId === q.id || q.reviews.some((r) => r.role === "finance_controller")}
                    className="rounded-full bg-gradient-to-r from-[#06B6D4] to-[#7C3AED] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-white disabled:opacity-40">
                    Finance approve
                  </button>
                  <button data-testid={`finance-approve-admin-${q.id.slice(-6)}`} onClick={() => approve(q.id, "admin")} disabled={busyId === q.id || q.reviews.some((r) => r.role === "admin")}
                    className="rounded-full bg-gradient-to-r from-[#F5B300] to-[#F97316] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-[#09090B] disabled:opacity-40">
                    Admin approve
                  </button>
                </div>
              )}
              {q.status === "approved" && (
                <button data-testid={`deal-open-${q.id.slice(-6)}`} onClick={async () => {
                  try { await axios.post(`${API}/dealroom/open/${q.id}`, {}, headers()); setDealRoom(q.id); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                }} className="mt-3 rounded-full bg-gradient-to-r from-[#F5B300] to-[#F97316] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-[#09090B]">
                  Open deal room
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const VendorComplianceAdmin = () => {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    axios.get(`${API}/admin/vendor-compliance`, headers()).then((r) => setItems(r.data.compliance)).catch(() => setItems([]));
  };
  useEffect(load, []);

  const review = async (userId, action) => {
    setBusyId(userId);
    try {
      await axios.patch(`${API}/admin/vendor-compliance/${userId}`, { action }, headers());
      toast.success(action === "approve" ? "Vendor verified ✅" : "Compliance rejected");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (items === null) return null;
  const pending = items.filter((i) => i.status === "submitted" || i.status === "under_review");
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="vendor-admin">
      <p className="font-display text-lg font-bold">🛡️ Vendor Compliance ({pending.length} pending)</p>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No compliance submissions yet.</p>
      ) : (
        <div className="mt-4 space-y-2.5">
          {items.map((v) => (
            <div key={v.id} data-testid={`vc-row-${v.id.slice(-6)}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold">{v.business_name}</p>
                <p className="truncate font-jbmono text-[10px] text-slate-500">{v.email} · {v.tax_id} · {v.country}{v.sanctions_flag ? " · 🚨 sanctions" : ""}</p>
              </div>
              <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                v.status === "approved" ? "bg-[#22C55E]/10 text-[#22C55E]" : v.status === "rejected" ? "bg-[#EF4444]/10 text-[#EF4444]" : "bg-[#F97316]/10 text-[#F97316]"
              }`}>{v.status}</span>
              {(v.status === "submitted" || v.status === "under_review") && (
                <>
                  <button data-testid={`vc-approve-${v.user_id.slice(-6)}`} onClick={() => review(v.user_id, "approve")} disabled={busyId === v.user_id} className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-white disabled:opacity-50">
                    Approve
                  </button>
                  <button data-testid={`vc-reject-${v.user_id.slice(-6)}`} onClick={() => review(v.user_id, "reject")} disabled={busyId === v.user_id} className="rounded-full border border-[#F97316]/40 px-4 py-1.5 text-[10px] font-bold text-[#F97316]">
                    Reject
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export const MastersAdmin = () => {
  const masters = useMasters();
  const [selected, setSelected] = useState("colleges");
  const [values, setValues] = useState([]);
  const [newValue, setNewValue] = useState("");
  const [saving, setSaving] = useState(false);

  const editable = Object.keys(masters).filter(
    (k) => Array.isArray(masters[k]) && (masters[k].length === 0 || typeof masters[k][0] === "string")
  );

  useEffect(() => {
    setValues(masters[selected] || []);
  }, [selected, masters]);

  const add = () => {
    const v = newValue.trim();
    if (v && !values.includes(v)) setValues([...values, v]);
    setNewValue("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/masters/${selected}`, { value: values }, headers());
      toast.success(`Master "${selected}" saved — live in every form now`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="masters-admin">
      <ErpStatus />
      <EnterpriseAdmin />
      <PermissionMatrix />
      <TicketConsole />
      <SessionTracker />
      <WorldMapSessions />
      <DataExport />
      <div className="flex justify-end">
        <a href={`${API}/admin/governance/reports/compliance?format=csv`} data-testid="download-compliance"
          className="rounded-full border border-white/15 px-4 py-2 font-jbmono text-[10px] font-bold uppercase tracking-wider text-slate-300 hover:border-[#06B6D4]/60 hover:text-white">
          📄 Download compliance report
        </a>
      </div>
      <FinanceConsole />
      <AuditorDashboard />
      <EmergencyAccess />
      <GovernanceAdmin />
      <BusinessAdmin />
      <IdVerificationsAdmin />
      <VendorComplianceAdmin />
      <CertificationsAdmin />
      <PayoutsAdmin />
      <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl">
        <p className="flex items-center gap-2 font-display text-lg font-bold">
          <DatabaseZap className="h-5 w-5 text-[#06B6D4]" /> Master Data
        </p>
        <p className="mt-1 text-xs text-slate-500">
          These lists power every dropdown, chip picker and search in the app — edits go live instantly.
        </p>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="master-keys">
          {editable.map((k) => (
            <button
              key={k}
              data-testid={`master-key-${k}`}
              onClick={() => setSelected(k)}
              className={`rounded-full border px-4 py-2 font-jbmono text-[11px] font-bold transition-colors ${
                selected === k ? "border-[#06B6D4] bg-[#06B6D4]/15 text-[#06B6D4]" : "border-white/10 text-slate-400 hover:text-white"
              }`}
            >
              {k} <span className="opacity-60">({masters[k].length})</span>
            </button>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-2" data-testid="master-values">
          {values.map((v) => (
            <span key={v} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-2 text-xs text-slate-200">
              {v}
              <button data-testid={`master-remove-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setValues(values.filter((x) => x !== v))} className="text-slate-500 hover:text-[#F97316]">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <span className="flex items-center gap-1">
            <input
              data-testid="master-add-input"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
              placeholder="+ Add value"
              className="w-36 rounded-full border border-dashed border-white/15 bg-transparent px-3.5 py-2 text-xs text-white placeholder:text-slate-600 outline-none focus:border-[#06B6D4]/60"
            />
            {newValue.trim() && (
              <button data-testid="master-add-btn" onClick={add} className="rounded-full bg-[#06B6D4]/15 p-2 text-[#06B6D4]">
                <Plus className="h-3 w-3" />
              </button>
            )}
          </span>
        </div>
        <button
          data-testid="master-save"
          onClick={save}
          disabled={saving}
          className="mt-5 flex items-center gap-2 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-7 py-3 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
        >
          {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : `Save ${selected}`}
        </button>
      </section>
    </div>
  );
};
